import { useState, useMemo } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import ShadcnHorizontalChart from './charts/ShadcnHorizontalChart'
import { SegmentedControl } from '@/components/shared/layout'
import { sanitizeUrl } from '@/lib/utils/stringUtils'
import { categorizeChannel } from '@/components/shared/ChannelsComponent'
import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowRight01Icon } from '@hugeicons/core-free-icons'
import {
  OVERVIEW_BREAKDOWN_LIMIT,
  OVERVIEW_LIST_CLASS,
} from '@/lib/constants/analyticsOverview'
import {
  OverviewBreakdownCard,
  getPeriodUniqueVisitors,
} from '@/components/shared/overview/OverviewBreakdownCard'

type TabType = 'Channels' | 'Sources' | 'Campaigns'

interface AcquisitionComponentProps {
  dashBoardData: any
  onFilterByParam?: (category: string, filter: string) => void
  className?: string
  /** Overview list-only mode: muted row bars, top N */
  overviewMode?: boolean
}

export default function AcquisitionComponent({
  dashBoardData,
  onFilterByParam,
  className = '',
  overviewMode = true,
}: AcquisitionComponentProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()

  const getBasePath = () => {
    if (params.workspaceId) return `/workspace/${params.workspaceId}`
    if (params.propertySlugId) return `/p/${params.propertySlugId}`
    if (params.domain) return `/d/${params.domain}`
    return ''
  }
  const basePath = getBasePath()

  const [currentTab, setCurrentTab] = useState<TabType>('Channels')

  const { labels, values } = useMemo(() => {
    const metrics = dashBoardData?.metrics
    if (!metrics) return { labels: [] as string[], values: [] as number[] }

    if (currentTab === 'Channels') {
      const channelData: Record<string, number> = {}
      // Every referrer/UTM row, not just the 10 largest: truncating before
      // grouping undercounted any channel made of many small referrers. Shares
      // are taken against the period's deduped total (the `total` prop below).
      const sourceData = metrics.aggregate?.filter((row: any) => row.referer_group_aggregate) || []

      sourceData.forEach((row: any) => {
        const channel = categorizeChannel(row.referer_group, row.utm_medium, row.utm_source)
        channelData[channel] = (channelData[channel] || 0) + Number(row.count)
      })

      const limit = overviewMode ? OVERVIEW_BREAKDOWN_LIMIT : 7
      const sorted = Object.entries(channelData).sort((a, b) => b[1] - a[1]).slice(0, limit)
      return {
        labels: sorted.map(([k]) => k),
        values: sorted.map(([, v]) => v),
      }
    }

    if (currentTab === 'Sources') {
      const sourceMap: Record<string, number> = {}
      const DIRECT = new Set(['', '(direct)', 'direct', 'unknown'])

      // Prefer UTM sources when present
      metrics.utmResults?.forEach((data: any) => {
        if (data.utm_source_aggregate === 'utm_source' && data.utm_source) {
          sourceMap[data.utm_source] = (sourceMap[data.utm_source] || 0) + (parseInt(data.count) || 0)
        }
      })

      if (Object.keys(sourceMap).length === 0) {
        metrics.aggregate
          ?.filter((row: any) => row.referer_group_aggregate)
          ?.forEach((row: any) => {
            const raw = row.referer_group
            const label = !raw || DIRECT.has(String(raw).toLowerCase()) ? 'Direct' : sanitizeUrl(raw)
            sourceMap[label] = (sourceMap[label] || 0) + Number(row.count)
          })
      }

      const limit = overviewMode ? OVERVIEW_BREAKDOWN_LIMIT : 7
      const sorted = Object.entries(sourceMap).sort((a, b) => b[1] - a[1]).slice(0, limit)
      return {
        labels: sorted.map(([k]) => k),
        values: sorted.map(([, v]) => v),
      }
    }

    // Campaigns
    const campaignMap: Record<string, number> = {}
    metrics.utmResults?.forEach((data: any) => {
      if (data.utm_campaign_aggregate === 'utm_campaign' && data.utm_campaign) {
        campaignMap[data.utm_campaign] =
          (campaignMap[data.utm_campaign] || 0) + (parseInt(data.count) || 0)
      }
    })
    const limit = overviewMode ? OVERVIEW_BREAKDOWN_LIMIT : 7
    const sorted = Object.entries(campaignMap).sort((a, b) => b[1] - a[1]).slice(0, limit)
    return {
      labels: sorted.map(([k]) => k),
      values: sorted.map(([, v]) => v),
    }
  }, [dashBoardData, currentTab, overviewMode])

  const navigateToChannels = () => {
    navigate(`${basePath}/channels${location.search}`)
  }

  const handleFilter = (title: string) => {
    if (!onFilterByParam) return
    if (currentTab === 'Channels') onFilterByParam('Channels', title)
    else if (currentTab === 'Sources') onFilterByParam('Source', title)
    else onFilterByParam('Campaign', title)
  }

  const tabs: { value: TabType; label: string }[] = [
    { value: 'Channels', label: 'Channels' },
    { value: 'Sources', label: 'Sources' },
    { value: 'Campaigns', label: 'Campaigns' },
  ]

  const isLoading = !dashBoardData?.metrics

  if (overviewMode) {
    return (
      <OverviewBreakdownCard
        className={className}
        title="Acquisition"
        tabs={tabs}
        tab={currentTab}
        onTabChange={setCurrentTab}
        dimensionLabel={currentTab === 'Channels' ? 'Channel' : currentTab === 'Sources' ? 'Source' : 'Campaign'}
        valueLabel="Visitors"
        // Channel names come out of categorizeChannel title-cased; the UI is sentence case.
        rows={labels.map((label, i) => ({
          label: currentTab === 'Channels' ? label.charAt(0) + label.slice(1).toLowerCase() : label,
          value: values[i] ?? 0,
        }))}
        total={getPeriodUniqueVisitors(dashBoardData?.metrics)}
        limit={5}
        isLoading={isLoading}
        onRowClick={handleFilter}
        footerLabel="View all channels"
        onFooterClick={navigateToChannels}
      />
    )
  }

  return (
    <Card className={className}>
      <CardHeader className="border-b border-border/40 pb-3">
        <div className="flex items-center justify-between gap-3 w-full">
          <span className="text-sm font-medium text-foreground shrink-0">Acquisition</span>
          <div className="flex items-center gap-2 min-w-0">
            <SegmentedControl
              options={tabs}
              value={currentTab}
              onChange={(v) => setCurrentTab(v)}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={navigateToChannels}
              className="view-all-btn shrink-0"
            >
              <span>View all</span>
              <HugeiconsIcon icon={ArrowRight01Icon} size={12} className="opacity-60" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {isLoading ? (
          <div className="space-y-2 py-2">
            {[0.9, 0.65, 0.5, 0.35, 0.25].map((w, i) => (
              <div key={i} className="h-8 rounded-sm bg-muted/40 animate-pulse" style={{ width: `${w * 100}%` }} />
            ))}
          </div>
        ) : labels.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground text-sm">
            No data to display. Try a different date range.
          </div>
        ) : (
          <div className={overviewMode ? OVERVIEW_LIST_CLASS : undefined}>
            <ShadcnHorizontalChart
              labels={labels}
              values={values}
              maxItems={overviewMode ? OVERVIEW_BREAKDOWN_LIMIT : 7}
              onBarClick={handleFilter}
              title={currentTab === 'Channels' ? 'Channel' : currentTab === 'Sources' ? 'Source' : 'Campaign'}
              valueLabel="Visitors"
              showFavicons={currentTab === 'Sources'}
              variant="underline"
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}
